import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { MaskedLines } from "@/components/site/primitives";
import { IMAGES } from "@/i18n";
import { Button } from "@/components/ui/button";

export const Hero = ({ onBook, onReadings }) => {
  const { t } = useLang();
  const ref = useRef(null);
  const [start, setStart] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setStart(true), 250);
    return () => clearTimeout(id);
  }, []);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="hero" ref={ref} className="relative min-h-screen overflow-hidden pt-[72px]">
      <div className="pointer-events-none absolute inset-0">
        {[...Array(28)].map((_, i) => (
          <span
            key={i}
            className="absolute rounded-full bg-gold/60 animate-twinkle"
            style={{
              top: `${(i * 37) % 100}%`,
              left: `${(i * 61) % 100}%`,
              width: i % 5 === 0 ? 2.5 : 1.5,
              height: i % 5 === 0 ? 2.5 : 1.5,
              animationDelay: `${(i % 6) * 0.7}s`,
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-8 items-center min-h-[calc(100vh-72px)] py-16">
        <motion.div style={{ y: yText }} className="relative z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={start ? { opacity: 1 } : {}}
            transition={{ duration: 1, delay: 0.1 }}
            className="font-serifluxe uppercase text-xs tracking-mystic text-gold mb-7"
            data-testid="hero-kicker"
          >
            {t.hero.kicker}
          </motion.p>

          <h1 className="font-display font-light text-sand leading-[0.98] text-4xl sm:text-6xl lg:text-7xl xl:text-[5rem]">
            <MaskedLines lines={[t.hero.h1]} start={start} lineClassName="italic" />
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={start ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Button
              onClick={onBook}
              className="rounded-full bg-rust hover:bg-rust/90 text-cream h-12 px-8 font-body tracking-wide text-sm transition-transform hover:scale-[0.97]"
              data-testid="hero-book-button"
            >
              {t.hero.cta}
            </Button>
            <button
              onClick={onReadings}
              className="group inline-flex items-center gap-2 text-sand/80 hover:text-gold transition-colors font-body text-sm tracking-wide"
              data-testid="hero-readings-button"
            >
              <span className="h-px w-8 bg-gold/60 group-hover:w-12 transition-all" />
              {t.hero.cta2}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={start ? { opacity: 1 } : {}}
            transition={{ duration: 1, delay: 1 }}
            className="mt-9 inline-flex items-center gap-2 text-gold/80 text-xs tracking-widest uppercase font-serifluxe"
          >
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
            {t.hero.badge}
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          animate={start ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 1.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto lg:mx-0 w-full max-w-[420px]"
        >
          <div className="relative overflow-hidden rounded-t-[220px] rounded-b-[28px] border border-gold/25 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
            <motion.img
              src={IMAGES.heroPortrait}
              alt="Monia"
              style={{ y: yImg, scale: scaleImg }}
              className="w-full h-[560px] sm:h-[640px] object-cover object-top"
              data-testid="hero-portrait"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ivy/70 via-transparent to-transparent" />
          </div>
          <div className="absolute -left-5 top-10 h-16 w-16 rounded-full border border-gold/40 backdrop-blur-sm bg-ivy/30 flex items-center justify-center text-gold text-xl animate-twinkle">
            ☾
          </div>
          <div className="absolute -right-4 bottom-16 h-12 w-12 rounded-full border border-gold/30 backdrop-blur-sm bg-espresso/40 flex items-center justify-center text-gold">
            ✶
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={start ? { opacity: 1 } : {}}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gold/60"
      >
        <span className="text-[10px] tracking-mystic uppercase font-serifluxe">scroll</span>
        <span className="h-8 w-px bg-gradient-to-b from-gold/60 to-transparent" />
      </motion.div>
    </section>
  );
};

export default Hero;
